import type { AnalyticsCheckout, AnalyticsCheckoutsResponse } from "@/@types/Analytics";
import Search from "@/assets/icons/Search";
import Divider from "@/components/shared/Divider/Divider";
import { PaginationRange } from "@/components/shared/PaginationRange/PaginationRange";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { checkoutTemplateLabel } from "@/utils/checkoutTemplates";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { HeaderTooltip } from "./ChartParts";

type Tone = "default" | "primary" | "danger";

const TONE_CLASS: Record<Tone, string> = {
  default: "text-colorText",
  primary: "text-primary font-medium",
  danger: "text-red-400",
};

interface Column {
  key: string;
  title: string;
  /** Sufixo das chaves `reports:tooltip_<id>_desc` e `_example`. */
  tooltipId?: string;
  tone?: Tone;
  render: (checkout: AnalyticsCheckout) => ReactNode;
}

const percent = (value?: number) => `${value?.toFixed(2) || "0"}%`;

function buildColumns(t: TFunction): Column[] {
  return [
    {
      key: "product",
      title: t("reports:product"),
      render: (c) => (
        <span className="block min-w-[200px] max-w-[280px] truncate" title={c.product?.name ?? undefined}>
          {c.product?.name || "-"}
        </span>
      ),
    },
    {
      key: "checkout",
      title: t("reports:checkout"),
      render: (c) => (
        <>
          <span className="block min-w-[200px] max-w-[280px] truncate font-medium" title={c.name}>
            {c.name}
          </span>
          {c.template && (
            <Badge variant="outline" className="mt-1 text-xs font-normal text-muted-foreground">
              {checkoutTemplateLabel(c.template)}
            </Badge>
          )}
        </>
      ),
    },
    {
      key: "approved_sales",
      title: t("reports:table_approved_sales"),
      tooltipId: "approved_sales",
      render: (c) => <span className="font-medium">{c.approved_sales || 0}</span>,
    },
    {
      key: "conversion_rate",
      title: t("reports:table_conversion_rate"),
      tooltipId: "conversion_rate",
      tone: "primary",
      render: (c) => c.conversion_rate_formatted || "0%",
    },
    {
      key: "payment_approval_rate",
      title: t("reports:table_payment_approval"),
      tooltipId: "payment_approval",
      render: (c) => c.payment_approval_rate_formatted || "0%",
    },
    {
      key: "aov",
      title: t("reports:table_aov"),
      tooltipId: "aov",
      render: (c) => c.average_order_value_formatted || "R$ 0,00",
    },
    {
      key: "orderbump_conversion",
      title: t("reports:table_orderbump_conv"),
      tooltipId: "orderbump_conv",
      render: (c) => percent(c.orderbump?.percentage),
    },
    {
      key: "upsell_conversion",
      title: t("reports:table_upsell_conv"),
      tooltipId: "upsell_conv",
      render: (c) => percent(c.upsell_downsell?.percentage),
    },
    {
      key: "orderbump_revenue",
      title: "Orderbump",
      tooltipId: "orderbump_extra",
      render: (c) => c.orderbump?.amount_formatted || "R$ 0,00",
    },
    {
      key: "upsell_revenue",
      title: "Upsell/Down",
      tooltipId: "upsell_extra",
      render: (c) => c.upsell_downsell?.amount_formatted || "R$ 0,00",
    },
    {
      key: "gross_revenue",
      title: t("reports:table_gross_revenue"),
      tooltipId: "gross_revenue",
      tone: "primary",
      render: (c) => c.gross_revenue_formatted || "R$ 0,00",
    },
    {
      key: "declined",
      title: t("reports:table_declined"),
      tooltipId: "declined",
      tone: "danger",
      render: (c) => c.declined_payments || 0,
    },
    {
      key: "refund_amount",
      title: t("reports:table_refunds"),
      tooltipId: "refunds",
      tone: "danger",
      render: (c) => c.refund?.amount_formatted || "R$ 0,00",
    },
    {
      key: "refund_rate",
      title: t("reports:table_refund_rate"),
      tooltipId: "refund_rate",
      render: (c) => c.refund?.rate_formatted || "0%",
    },
    {
      key: "chargeback_amount",
      title: t("reports:table_chargebacks"),
      tooltipId: "chargebacks",
      tone: "danger",
      render: (c) => c.chargeback?.amount_formatted || "R$ 0,00",
    },
    {
      key: "chargeback_rate",
      title: t("reports:table_chargeback_rate"),
      tooltipId: "chargeback_rate",
      render: (c) => c.chargeback?.rate_formatted || "0%",
    },
    {
      key: "net_revenue",
      title: t("reports:table_net_revenue"),
      tooltipId: "net_revenue",
      render: (c) => <span className="text-primary font-bold">{c.net_revenue_formatted || "R$ 0,00"}</span>,
    },
  ];
}

interface CheckoutMetricsTableProps {
  data?: AnalyticsCheckoutsResponse;
  isLoading: boolean;
  onPageChange: (page: number) => void;
}

export function CheckoutMetricsTable({ data, isLoading, onPageChange }: CheckoutMetricsTableProps) {
  const { t } = useTranslation("reports");
  const columns = useMemo(() => buildColumns(t), [t]);
  const rows = data?.data ?? [];

  return (
    <Card className="w-full overflow-x-auto">
      <div className="flex md:items-center flex-col md:flex-row w-full px-4 py-6 justify-between max-md:gap-3">
        <h1 className="text-base font-semibold">{t("reports:metrics_table_title")}</h1>
      </div>

      <Divider />

      <div className="overflow-x-auto">
        <table className="w-full min-w-[2000px]">
          <thead>
            <tr className="border-b border-colorBorder bg-zinc-900/50">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap"
                >
                  <div className="flex items-center">
                    {column.title}
                    {column.tooltipId && (
                      <HeaderTooltip
                        title={column.title}
                        description={t(`reports:tooltip_${column.tooltipId}_desc`)}
                        example={t(`reports:tooltip_${column.tooltipId}_example`)}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 3 }).map((_, index) => (
                <tr key={index} className="border-b border-colorBorder">
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3">
                      <Skeleton className="h-4 w-16" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length > 0 ? (
              rows.map((checkout, index) => (
                <tr
                  key={checkout.id || index}
                  className="border-b border-colorBorder hover:bg-zinc-900/30 transition-colors"
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3">
                      <div className={cn("text-sm", TONE_CLASS[column.tone ?? "default"])}>
                        {column.render(checkout)}
                      </div>
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <Search />
                    <p className="text-sm font-medium text-colorText">
                      {t("reports:no_metrics_found")}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t("reports:no_data_for_period")}
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data?.meta && data.meta.last_page > 1 && (
        <div className="flex justify-center py-4">
          <PaginationRange
            currentPage={data.meta.current_page}
            totalPages={data.meta.last_page}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Card>
  );
}
